import { jsPDF } from 'jspdf';
import { Building, ApartmentStatus, Apartment } from '../types/building';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { getCurrentUser } from '../lib/localDatabase';
import { getApartmentLabel } from './getApartmentLabel';
import { getBuildingPhotos } from '../lib/photoStorage';

// Professional color palette
const colors = {
  primary: [37, 99, 235],    // blue-600
  secondary: [79, 70, 229],  // indigo-600
  success: [22, 163, 74],    // green-600
  warning: [234, 179, 8],    // yellow-600
  danger: [220, 38, 38],     // red-600
  gray: {
    100: [243, 244, 246],
    200: [229, 231, 235],
    300: [209, 213, 219],
    400: [156, 163, 175],
    500: [107, 114, 128],
    600: [75, 85, 99],
    700: [55, 65, 81],
    800: [31, 41, 55],
    900: [17, 24, 39],
  }
};

// Status colors
const statusColors: Record<ApartmentStatus, [number, number, number]> = {
  pending: colors.warning,
  in_progress: colors.primary,
  completed: colors.success,
  completed_with_cable: colors.success,
  completed_no_measurement: colors.success,
  tenant_absent: colors.warning,
  access_denied: colors.danger,
  tenant_affected_no_access: colors.danger,
  tenant_affected_with_cable: colors.warning,
  present_no_access: colors.danger,
  appointment_cancelled: colors.gray[500],
  aborted: [168, 85, 247], // purple-600
};

// Status labels
const statusLabels: Record<ApartmentStatus, string> = {
  pending: 'Ausstehend',
  in_progress: 'In Bearbeitung',
  completed: '1 - Installation vollständig',
  completed_with_cable: '1-K - Installation mit Kabelzug',
  completed_no_measurement: '10 - Installation ohne Messung',
  tenant_absent: '2 - Mieter nicht anwesend',
  access_denied: '3 - Zutritt verweigert',
  tenant_affected_no_access: '4 - Installation nicht möglich',
  tenant_affected_with_cable: '4-K - Installation mit Kabelzug nicht möglich',
  present_no_access: '5 - Anschluss nicht zugänglich',
  appointment_cancelled: '6 - Termin abgesagt',
  aborted: 'Abbruch',
};

// Draw a card with shadow effect
const drawCard = (doc: jsPDF, x: number, y: number, width: number, height: number) => {
  // Card shadow
  doc.setFillColor(0, 0, 0, 0.1);
  doc.roundedRect(x + 2, y + 2, width, height, 3, 3, 'F');
  
  // Card background
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...colors.gray[200]);
  doc.roundedRect(x, y, width, height, 3, 3, 'FD');
};

// Draw text that stays within bounds and returns the new Y position
const drawBoundedText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number = 10,
  fontStyle: string = 'normal'
): number => {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', fontStyle);
  const lines = doc.splitTextToSize(text, maxWidth);
  const lineHeight = fontSize * 0.5;
  
  lines.forEach((line: string, index: number) => {
    doc.text(line, x, y + (index * lineHeight));
  });
  
  return y + (lines.length * lineHeight);
};

// Add metadata to PDF for better import
const addMetadata = (doc: jsPDF, building: Building) => {
  // Add custom metadata as a hidden text field
  const metadata = {
    type: 'InstallTrack Pro Export',
    version: '1.0',
    timestamp: new Date().toISOString(),
    building: {
      id: building.id,
      name: building.name,
      address: building.address,
      floors: building.floors.map(floor => ({
        id: floor.id,
        number: floor.number,
        apartments: floor.apartments.map(apt => ({
          id: apt.id,
          number: apt.number,
          formattedNumber: apt.formattedNumber,
          status: apt.status,
          notes: apt.notes,
          areaType: apt.areaType,
          abortReason: apt.abortReason,
          technician: apt.technician,
          konzernId: apt.konzernId,
          lastUpdated: apt.lastUpdated?.toISOString()
        }))
      }))
    }
  };

  // Add metadata as a hidden text field
  doc.setFontSize(1);
  doc.setTextColor(255, 255, 255); // White text (invisible)
  doc.text(
    `<!--INSTALLTRACK_METADATA:${JSON.stringify(metadata)}-->`,
    0,
    0
  );
};

// Add a watermark with GPS and timestamp data
const addPhotoWatermark = (
  doc: jsPDF, 
  x: number, 
  y: number, 
  width: number, 
  photo: any
) => {
  // Skip if no GPS or timestamp data
  if (!photo.gpsLocation && !photo.timestamp) return;
  
  const watermarkHeight = 20;
  const watermarkY = y - watermarkHeight;
  
  // Draw semi-transparent background
  doc.setFillColor(0, 0, 0, 0.7);
  doc.rect(x, watermarkY, width, watermarkHeight, 'F');
  
  // Set text color to white
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  
  let textY = watermarkY + 8;
  
  // Add timestamp if available
  if (photo.timestamp) {
    const timestampText = `Datum/Zeit: ${format(photo.timestamp, 'dd.MM.yyyy HH:mm:ss', { locale: de })}`;
    doc.text(timestampText, x + 5, textY);
    textY += 8;
  }
  
  // Add GPS location if available
  if (photo.gpsLocation) {
    const coordsText = `Koordinaten: ${photo.gpsLocation.latitude.toFixed(6)}, ${photo.gpsLocation.longitude.toFixed(6)}`;
    doc.text(coordsText, x + 5, textY);
    
    // Add address on next line if available
    if (photo.gpsLocation.address) {
      textY += 8;
      const addressText = `Adresse: ${photo.gpsLocation.address}`;
      // Truncate address if too long
      const maxWidth = width - 10;
      const truncatedAddress = doc.splitTextToSize(addressText, maxWidth)[0];
      doc.text(truncatedAddress, x + 5, textY);
    }
  }
};

export const generatePDF = async (building: Building) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  const currentUser = getCurrentUser();

  // Add metadata for import
  addMetadata(doc, building);

  // Header
  const addHeader = (pageNum: number, floorLabel?: string) => {
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('InstallTrack Pro', margin, 25);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    if (floorLabel) {
      doc.text(`${building.name} - ${floorLabel}`, margin, 35);
    } else {
      doc.text('Installationsdokumentation', margin, 35);
    }
  };

  // Footer
  const addFooter = (pageNum: number, totalPages: number) => {
    doc.setFillColor(...colors.gray[100]);
    doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(...colors.gray[600]);
    doc.text(`Seite ${pageNum} von ${totalPages}`, margin, pageHeight - 8);
    doc.text(format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de }), pageWidth - margin, pageHeight - 8, { align: 'right' });
  };

  // Cover page
  addHeader(1);

  // Building info card
  let yPos = 60;
  drawCard(doc, margin, yPos, contentWidth, 80);
  
  doc.setTextColor(...colors.gray[900]);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(building.name, margin + 15, yPos + 25);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.gray[600]);
  doc.text(building.address, margin + 15, yPos + 45);
  
  if (currentUser) {
    doc.setFontSize(12);
    doc.text(`Techniker: ${currentUser.name}`, margin + 15, yPos + 65);
    if (currentUser.company) {
      doc.text(`Firma: ${currentUser.company}`, margin + 15, yPos + 75);
    }
  }

  // Progress overview
  yPos += 100;
  drawCard(doc, margin, yPos, contentWidth, 60);
  
  const completedCount = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.filter(
      apt => ['completed', 'completed_with_cable', 'completed_no_measurement'].includes(apt.status)
    ).length,
    0
  );
  
  const totalCount = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.length,
    0
  );
  
  const completionPercentage = totalCount > 0
    ? Math.round((completedCount / totalCount) * 100)
    : 0;

  doc.setTextColor(...colors.gray[900]);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Installationsfortschritt', margin + 15, yPos + 25);
  
  // Progress bar
  const barWidth = contentWidth - 30;
  const barHeight = 8;
  const barX = margin + 15;
  const barY = yPos + 35;
  
  // Background
  doc.setFillColor(...colors.gray[200]);
  doc.roundedRect(barX, barY, barWidth, barHeight, 2, 2, 'F');
  
  // Progress
  if (completionPercentage > 0) {
    doc.setFillColor(...colors.success);
    doc.roundedRect(barX, barY, (barWidth * completionPercentage) / 100, barHeight, 2, 2, 'F');
  }
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `${completedCount} von ${totalCount} Wohneinheiten abgeschlossen (${completionPercentage}%)`,
    margin + 15,
    yPos + 55
  );

  // Get all photos
  const buildingPhotos = await getBuildingPhotos(building.id);
  const photosByApartment = buildingPhotos.reduce((acc, photo) => {
    if (!acc[photo.apartmentId]) {
      acc[photo.apartmentId] = [];
    }
    acc[photo.apartmentId].push(photo);
    return acc;
  }, {} as Record<string, any[]>);

  // Each floor gets its own page
  building.floors
    .sort((a, b) => b.number - a.number)
    .forEach(floor => {
      // Start new page for each floor
      doc.addPage();
      const floorLabel = floor.number === 0 ? 'EG' :
        floor.number < 0 ? `${Math.abs(floor.number)}. UG` :
        `${floor.number}. OG`;
      
      addHeader(doc.getNumberOfPages(), floorLabel);
      yPos = 60;

      // Floor summary card
      drawCard(doc, margin, yPos, contentWidth, 60);
      
      doc.setTextColor(...colors.gray[900]);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(floorLabel, margin + 15, yPos + 25);
      
      const floorCompletedCount = floor.apartments.filter(
        apt => ['completed', 'completed_with_cable', 'completed_no_measurement'].includes(apt.status)
      ).length;
      
      const floorTotalCount = floor.apartments.length;
      const floorPercentage = floorTotalCount > 0 ? Math.round((floorCompletedCount / floorTotalCount) * 100) : 0;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${floorCompletedCount} von ${floorTotalCount} Wohneinheiten abgeschlossen (${floorPercentage}%)`,
        margin + 15,
        yPos + 45
      );

      // Apartments
      floor.apartments.forEach((apt, index) => {
        yPos += 80;
        
        // Check if we need a new page for this apartment
        if (yPos > pageHeight - 100) {
          doc.addPage();
          addHeader(doc.getNumberOfPages(), floorLabel);
          yPos = 60;
        }

        // Apartment card
        const photos = photosByApartment[apt.id] || [];
        const cardHeight = 120; // Fixed height for consistent layout
        drawCard(doc, margin, yPos, contentWidth, cardHeight);
        
        // Apartment number and status
        doc.setTextColor(...colors.gray[900]);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`${getApartmentLabel(apt)} ${apt.formattedNumber}`, margin + 15, yPos + 20);
        
        // Status indicator
        const [r, g, b] = statusColors[apt.status];
        doc.setFillColor(r, g, b);
        doc.circle(margin + 15, yPos + 35, 3, 'F');
        
        // Status text
        doc.setTextColor(...colors.gray[700]);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(statusLabels[apt.status], margin + 25, yPos + 35);

        // Additional information
        let infoY = yPos + 55;
        
        if (apt.technician) {
          doc.text(`Techniker: ${apt.technician}`, margin + 15, infoY);
          infoY += 10;
        }
        
        if (apt.konzernId) {
          doc.text(`Konzern ID: ${apt.konzernId}`, margin + 15, infoY);
          infoY += 10;
        }
        
        if (apt.notes) {
          const maxWidth = contentWidth - 30;
          const lines = doc.splitTextToSize(apt.notes, maxWidth);
          lines.forEach((line: string) => {
            doc.text(line, margin + 15, infoY);
            infoY += 10;
          });
        }

        // Photo links
        if (photos.length > 0) {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Fotos: ${photos.length}`, margin + 15, infoY);
          infoY += 10;
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.primary);
          
          photos.forEach((photo, photoIndex) => {
            const photoText = `Foto ${photoIndex + 1} vom ${format(photo.createdAt, 'dd.MM.yyyy', { locale: de })}`;
            doc.textWithLink(photoText, margin + 15, infoY, { url: photo.url });
            infoY += 8;
          });
        }

        // Last update info
        if (apt.lastUpdated) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(...colors.gray[500]);
          doc.text(
            `Letzte Aktualisierung: ${format(apt.lastUpdated, 'dd.MM.yyyy HH:mm', { locale: de })}`,
            margin + 15,
            yPos + cardHeight - 10
          );
        }

        yPos += cardHeight + 10;
      });
    });

  // Add photo pages if there are photos
  if (buildingPhotos.length > 0) {
    // Add a photo gallery section
    doc.addPage();
    addHeader(doc.getNumberOfPages(), 'Fotodokumentation');
    
    doc.setTextColor(...colors.gray[900]);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Fotodokumentation', margin, 60);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gesamtanzahl der Fotos: ${buildingPhotos.length}`, margin, 75);
    
    // Add photos with metadata
    let photoY = 90;
    const photosPerPage = 2; // Show 2 photos per page
    let photoCount = 0;
    
    for (const photo of buildingPhotos) {
      // Find which apartment this photo belongs to
      let apartmentInfo = '';
      for (const floor of building.floors) {
        const apartment = floor.apartments.find(apt => apt.id === photo.apartmentId);
        if (apartment) {
          const floorLabel = floor.number === 0 ? 'EG' :
            floor.number < 0 ? `${Math.abs(floor.number)}. UG` :
            `${floor.number}. OG`;
          apartmentInfo = `${floorLabel}, ${getApartmentLabel(apartment)} ${apartment.formattedNumber}`;
          break;
        }
      }
      
      // Check if we need a new page
      if (photoCount % photosPerPage === 0 && photoCount > 0) {
        doc.addPage();
        addHeader(doc.getNumberOfPages(), 'Fotodokumentation');
        photoY = 60;
      }
      
      // Photo card height
      const photoCardHeight = 180;
      
      // Draw photo card
      drawCard(doc, margin, photoY, contentWidth, photoCardHeight);
      
      // Add photo info
      doc.setTextColor(...colors.gray[900]);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Foto ${photoCount + 1}`, margin + 10, photoY + 20);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      let infoY = photoY + 35;
      
      // Add apartment info if available
      if (apartmentInfo) {
        doc.text(`Wohneinheit: ${apartmentInfo}`, margin + 10, infoY);
        infoY += 12;
      }
      
      // Add date
      doc.text(`Aufnahmedatum: ${format(photo.createdAt, 'dd.MM.yyyy HH:mm', { locale: de })}`, margin + 10, infoY);
      infoY += 12;
      
      // Add location if available
      if (photo.location) {
        doc.text(`Ort/Raum: ${photo.location}`, margin + 10, infoY);
        infoY += 12;
      }
      
      // Add description if available
      if (photo.description) {
        doc.text(`Beschreibung: ${photo.description}`, margin + 10, infoY);
        infoY += 12;
      }
      
      // Add notes if available
      if (photo.notes) {
        const maxWidth = contentWidth - 20;
        doc.text(`Notizen: `, margin + 10, infoY);
        infoY += 6;
        
        const lines = doc.splitTextToSize(photo.notes, maxWidth);
        lines.forEach((line: string, i: number) => {
          if (i < 3) { // Limit to 3 lines to avoid overflow
            doc.text(line, margin + 10, infoY);
            infoY += 10;
          }
        });
      }
      
      // Add GPS data if available
      if (photo.gpsLocation) {
        doc.text(`Koordinaten: ${photo.gpsLocation.latitude.toFixed(6)}, ${photo.gpsLocation.longitude.toFixed(6)}`, margin + 10, infoY);
        infoY += 12;
        
        if (photo.gpsLocation.address) {
          const maxWidth = contentWidth - 20;
          const addressLines = doc.splitTextToSize(`Adresse: ${photo.gpsLocation.address}`, maxWidth);
          addressLines.forEach((line: string, i: number) => {
            if (i < 2) { // Limit to 2 lines
              doc.text(line, margin + 10, infoY);
              infoY += 10;
            }
          });
        }
      }
      
      // Add timestamp if available
      if (photo.timestamp) {
        doc.text(`Zeitstempel: ${format(photo.timestamp, 'dd.MM.yyyy HH:mm:ss', { locale: de })}`, margin + 10, infoY);
      }
      
      // Add photo image placeholder with watermark
      const photoX = margin + 10;
      let photoY = photoY + photoCardHeight - 60;
      const photoWidth = contentWidth - 20;
      const photoHeight = 50;
      
      // Photo placeholder
      doc.setFillColor(240, 240, 240);
      doc.rect(photoX, photoY, photoWidth, photoHeight, 'F');
      
      // Add text indicating this is a placeholder
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(10);
      doc.text('Foto im PDF nicht enthalten - Siehe Originaldatei', photoX + photoWidth/2, photoY + photoHeight/2, { align: 'center' });
      
      // Add watermark with GPS and timestamp data
      if (photo.gpsLocation || photo.timestamp) {
        addPhotoWatermark(doc, photoX, photoY, photoWidth, photo);
      }
      
      // Increment counters
      photoY += photoCardHeight + 20;
      photoCount++;
    }
  }

  // Add footers to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  // Save the PDF
  doc.save(`${building.name}_Installation_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};